import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Users, Briefcase, Building2, Loader2, Clock } from 'lucide-react';
import { globalSearch, type GlobalSearchResult } from '@/lib/api';
import { cn } from '@/lib/utils';

const RECENT_SEARCHES_KEY = 'crm_recent_searches';
const MAX_RECENT = 5;

interface RecentSearch {
  query: string;
  timestamp: number;
}

function getRecentSearches(): RecentSearch[] {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function addRecentSearch(query: string) {
  const recent = getRecentSearches().filter((r) => r.query !== query);
  recent.unshift({ query, timestamp: Date.now() });
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}

function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query || query.length < 2) return text;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part) ? (
      <mark key={i} className="bg-emerald-500/30 text-text-primary rounded-sm px-0.5">
        {part}
      </mark>
    ) : (
      part
    ),
  );
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GlobalSearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Build flat list of all results for keyboard navigation
  const allItems: Array<{ type: 'candidate' | 'job' | 'company' | 'recent'; id: string; label: string; sub: string; path: string }> = [];

  if (query.length < 2 && recentSearches.length > 0) {
    for (const r of recentSearches) {
      allItems.push({
        type: 'recent',
        id: r.query,
        label: r.query,
        sub: 'Recent search',
        path: '',
      });
    }
  }

  if (results) {
    for (const c of results.candidates) {
      allItems.push({
        type: 'candidate',
        id: c.id,
        label: `${c.firstName} ${c.lastName}`,
        sub: [c.currentJobTitle, c.currentLocation].filter(Boolean).join(' - ') || 'Candidate',
        path: `/candidates/${c.id}`,
      });
    }
    for (const j of results.jobs) {
      allItems.push({
        type: 'job',
        id: j.id,
        label: j.title,
        sub: [j.company?.name, j.status].filter(Boolean).join(' - '),
        path: `/jobs/${j.id}`,
      });
    }
    for (const c of results.companies) {
      allItems.push({
        type: 'company',
        id: c.id,
        label: c.name,
        sub: c.industry || 'Company',
        path: `/companies/${c.id}`,
      });
    }
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery('');
      setResults(null);
      setSelectedIndex(0);
      setRecentSearches(getRecentSearches());
    }
  }, [open]);

  const doSearch = useCallback(async (term: string) => {
    if (term.length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await globalSearch(term);
      setResults(data);
      setSelectedIndex(0);
    } catch {
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(value), 250);
  };

  const selectItem = (item: (typeof allItems)[number]) => {
    if (item.type === 'recent') {
      setQuery(item.label);
      doSearch(item.label);
      return;
    }
    if (query.length >= 2) addRecentSearch(query);
    setOpen(false);
    navigate(item.path);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, allItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && allItems[selectedIndex]) {
      e.preventDefault();
      selectItem(allItems[selectedIndex]);
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'candidate': return <Users className="h-4 w-4 text-emerald-500 flex-shrink-0" />;
      case 'job': return <Briefcase className="h-4 w-4 text-blue-500 flex-shrink-0" />;
      case 'company': return <Building2 className="h-4 w-4 text-purple-500 flex-shrink-0" />;
      case 'recent': return <Clock className="h-4 w-4 text-text-tertiary flex-shrink-0" />;
      default: return null;
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50" onClick={() => setOpen(false)} />
      <div className="fixed left-1/2 top-[20%] z-50 w-full max-w-lg -translate-x-1/2">
        <div className="rounded-xl border border-white/10 bg-bg-secondary shadow-2xl overflow-hidden">
          <div className="flex items-center gap-3 px-4 border-b border-white/10">
            <Search className="h-5 w-5 text-text-tertiary flex-shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search candidates, jobs, companies..."
              className="flex-1 py-4 bg-transparent text-text-primary placeholder:text-text-tertiary outline-none text-sm"
            />
            {loading && <Loader2 className="h-4 w-4 animate-spin text-text-tertiary" />}
            <kbd className="px-1.5 py-0.5 text-xs text-text-tertiary bg-white/10 rounded">ESC</kbd>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {query.length < 2 && recentSearches.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-text-tertiary">
                Type at least 2 characters to search...
              </div>
            )}

            {query.length < 2 && recentSearches.length > 0 && (
              <div className="py-2">
                <div className="px-4 py-1.5 text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  Recent Searches
                </div>
                {recentSearches.map((r, i) => (
                  <button
                    key={r.query}
                    onClick={() => selectItem(allItems[i])}
                    onMouseEnter={() => setSelectedIndex(i)}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                      selectedIndex === i ? 'bg-white/5' : 'hover:bg-white/5',
                    )}
                  >
                    <Clock className="h-4 w-4 text-text-tertiary flex-shrink-0" />
                    <span className="text-sm text-text-primary">{r.query}</span>
                  </button>
                ))}
              </div>
            )}

            {query.length >= 2 && !loading && allItems.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-text-tertiary">
                No results found for &quot;{query}&quot;
              </div>
            )}

            {allItems.length > 0 && query.length >= 2 && (
              <div className="py-2">
                {results?.candidates && results.candidates.length > 0 && (
                  <>
                    <div className="px-4 py-1.5 text-xs font-medium text-text-tertiary uppercase tracking-wider">
                      Candidates
                    </div>
                    {results.candidates.map((c, i) => {
                      const idx = i;
                      const item = allItems[idx];
                      return (
                        <button
                          key={c.id}
                          onClick={() => selectItem(item)}
                          onMouseEnter={() => setSelectedIndex(idx)}
                          className={cn(
                            'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                            selectedIndex === idx ? 'bg-white/5' : 'hover:bg-white/5',
                          )}
                        >
                          {getIcon('candidate')}
                          <div className="min-w-0">
                            <div className="text-sm text-text-primary truncate">
                              {highlightMatch(`${c.firstName} ${c.lastName}`, query)}
                            </div>
                            <div className="text-xs text-text-tertiary truncate">
                              {[c.currentJobTitle, c.currentLocation].filter(Boolean).join(' - ') || 'Candidate'}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </>
                )}

                {results?.jobs && results.jobs.length > 0 && (
                  <>
                    <div className="px-4 py-1.5 text-xs font-medium text-text-tertiary uppercase tracking-wider mt-1">
                      Jobs
                    </div>
                    {results.jobs.map((j, i) => {
                      const idx = (results?.candidates?.length || 0) + i;
                      const item = allItems[idx];
                      return (
                        <button
                          key={j.id}
                          onClick={() => selectItem(item)}
                          onMouseEnter={() => setSelectedIndex(idx)}
                          className={cn(
                            'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                            selectedIndex === idx ? 'bg-white/5' : 'hover:bg-white/5',
                          )}
                        >
                          {getIcon('job')}
                          <div className="min-w-0">
                            <div className="text-sm text-text-primary truncate">
                              {highlightMatch(j.title, query)}
                            </div>
                            <div className="text-xs text-text-tertiary truncate">
                              {[j.company?.name, j.status].filter(Boolean).join(' - ')}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </>
                )}

                {results?.companies && results.companies.length > 0 && (
                  <>
                    <div className="px-4 py-1.5 text-xs font-medium text-text-tertiary uppercase tracking-wider mt-1">
                      Companies
                    </div>
                    {results.companies.map((c, i) => {
                      const idx = (results?.candidates?.length || 0) + (results?.jobs?.length || 0) + i;
                      const item = allItems[idx];
                      return (
                        <button
                          key={c.id}
                          onClick={() => selectItem(item)}
                          onMouseEnter={() => setSelectedIndex(idx)}
                          className={cn(
                            'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                            selectedIndex === idx ? 'bg-white/5' : 'hover:bg-white/5',
                          )}
                        >
                          {getIcon('company')}
                          <div className="min-w-0">
                            <div className="text-sm text-text-primary truncate">
                              {highlightMatch(c.name, query)}
                            </div>
                            <div className="text-xs text-text-tertiary truncate">
                              {c.industry || 'Company'}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
